import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCw } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// Real, manually-set fleet BEHAVIOR inputs -- alert thresholds, matching
// radius, feature toggles, and fuel-optimization knobs. These are already
// live in production logic today (motive_sync.py/the alerts scripted API/
// match-truck-for-load/find-next-load/trip_planning.py all read these same
// property names) -- split out from the actual dollar-cost inputs on Fleet
// Cost Settings (FleetCostSettings.jsx) so "what does a load really cost"
// and "how does the platform behave" aren't mixed on one screen. Backed by
// plain system_properties rows, read/written through the generic
// dynamic_api table endpoint like any other table -- no dedicated backend
// route needed for a settings form. No protected (super_admin-only)
// fields here -- those are all hard spend ceilings, which stayed on Fleet
// Cost Settings.
//
// type drives both rendering (currency shows a "$" prefix, boolean shows
// a toggle switch instead of a number input) and validation (only
// currency/number fields need the NaN check). section groups fields into
// their own card with a heading, in FIELD_SECTIONS order.
const FIELDS = [
  {
    key: "fleet.min_miles_between_fuel_stops", label: "Minimum miles between fuel stops", type: "number", unit: "miles",
    hint: "Floor on how close together two fuel stops can be scheduled when the optimizer is chasing a marginally cheaper station further down the route (plan-full-trip, match-truck-for-load, explain-route-choice, and the live in-transit re-check all respect this). 0 disables the floor entirely -- today's exact behavior. Does NOT skip a genuinely mandatory stop (tank near its safety reserve); it only stops the optimizer from CHOOSING to schedule a discretionary stop implausibly close to the one before it for a small per-gallon savings.",
    section: "Fuel Optimization",
  },

  {
    key: "fleet.route_deviation_alert_miles", label: "Off-route alert distance", type: "number", unit: "miles",
    hint: "How far a truck's GPS position can drift from its planned route before the off_route alert fires on the Alerts page. Two consecutive off-route readings (about 2 minutes apart) are required before it triggers, so a single bad GPS ping won't cause a false alarm -- this setting only controls the distance, not that debounce count.",
    section: "Alerts & Thresholds",
  },
  {
    key: "fleet.toll_overage_alert_usd", label: "Toll overage alert amount", type: "currency", unit: "over planned",
    hint: "How far a truck's real, already-paid tolls (from actual toll-pass detection) can exceed the chosen route's own planned toll estimate before the toll_overage alert fires. Catches a truck that's taken a materially more expensive path than the one it was quoted for -- lower this to catch smaller overages sooner, raise it to ignore normal toll-estimate noise.",
    section: "Alerts & Thresholds",
  },
  {
    key: "fleet.motive_stale_alert_minutes", label: "Motive sync stale alert", type: "number", unit: "minutes",
    hint: "How long it's been since the last successful Motive sync before the fleet-wide motive_sync_stale alert fires. Once tripped, every truck's GPS position and HOS hours may be stale until the next sync completes. Keep this a bit above the real sync interval (currently every 3 minutes) -- too low and normal jitter trips it constantly, too high and a genuine outage goes unnoticed for a while.",
    section: "Alerts & Thresholds",
  },
  {
    key: "fleet.manual_driver_assignment_grace_minutes", label: "Manual assignment grace period", type: "number", unit: "minutes",
    hint: "How long a dispatcher's manual driver reassignment is protected from being silently overwritten by the next scheduled Motive sync. Exists because Motive's own truck-driver pairing can briefly disagree with a fresh manual change until Motive's systems catch up -- raise this if manual reassignments still get reverted too often, lower it if stale manual overrides are lingering longer than they should.",
    section: "Alerts & Thresholds",
  },
  {
    key: "fleet.eta_drift_alert_hours", label: "ETA drift alert threshold", type: "number", unit: "hours",
    hint: "How far a truck's live ETA can drift from what it was originally quoted before the eta_drift alert fires and a row opens in ETA Drift History. The same setting both the live Alerts page and the ETA Drift History job read -- lower it to catch smaller schedule slips sooner, raise it to ignore normal simulation noise (traffic, a slightly longer fuel stop).",
    section: "Alerts & Thresholds",
  },
  {
    key: "fleet.eta_drift_critical_hours", label: "ETA drift critical threshold", type: "number", unit: "hours",
    hint: "How far a drift has to grow past the ETA drift alert threshold above before it escalates from a warning to a critical alert, on both the live Alerts page and Control Tower's Fleet Health strip. Set this well above the alert threshold -- if the two are close together, nearly every warning-level drift would immediately also count as critical.",
    section: "Alerts & Thresholds",
  },

  {
    key: "fleet.truck_search_radius_miles", label: "Truck search radius", type: "number", unit: "miles",
    hint: "How wide broker-call matching and find-next-load search for candidate trucks around a load's pickup location. Wider surfaces more candidates (useful in sparse regions) but adds more HERE routing calls per search and can pull in trucks too far away to realistically compete on cost -- narrower keeps results tight and cheap but can miss a genuinely good match in a thin market.",
    section: "Matching",
  },

  {
    key: "fleet.lane_history_radius_miles", label: "Lane history search radius", type: "number", unit: "miles",
    hint: "How far a past load's real origin and destination can be from a new load's origin/destination and still count as \"the same lane\" on the Recent Loads on This Lane panel (Check Load / Log Broker Call). Matched by real distance, not by matching the city name as text -- a full street address a few miles outside downtown still counts. Wider surfaces more history in a sparse region but can start pulling in genuinely different lanes; narrower keeps it strictly to the same metro area.",
    section: "Lane History",
  },
  {
    key: "fleet.lane_history_lookback_days", label: "Lane history lookback window", type: "number", unit: "days",
    hint: "How far back the Recent Loads on This Lane panel looks for past loads on the same lane. A rate from outside this window never shows, even if it's the only one on file for that lane -- keeps the reference price relevant to today's market instead of surfacing something stale.",
    section: "Lane History",
  },

  {
    key: "fleet.track_unassigned_truck_miles", label: "Track idle/unassigned truck miles", type: "boolean",
    hint: "When on, every real mile a truck drives with no in-transit load is logged -- the raw signal behind the Idle Miles page (deadhead/wasted driving by truck). Turn this off only if this deployment doesn't want that tracking running at all; it doesn't affect anything else, just whether this one data feed is collected.",
    section: "Feature Toggles",
  },

  // ---- Auto-Confirm (automatic pickup/delivery from GPS stop detection) ----
  {
    key: "fleet.autoconfirm_offhours_mode", label: "Auto-confirm mode", type: "select",
    options: [{ value: "shadow", label: "Shadow (observe only -- never writes)" }, { value: "active", label: "Active (writes pickup/delivery)" }],
    default: "shadow",
    hint: "SHADOW records what it WOULD confirm but never changes a load (safe for measuring accuracy). ACTIVE actually stamps the load's pickup/delivery from GPS evidence (truck dwelled at the facility then drove materially away), using the system user, with all the guards below. The instant kill switch: set this back to Shadow.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_enabled", label: "Auto-confirm enabled (master)", type: "boolean",
    hint: "Master on/off for the whole auto-confirm engine. With this off, neither pickup nor delivery auto-confirms regardless of the other switches.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_pickup_enabled", label: "Auto-confirm pickups", type: "boolean",
    hint: "Allow automatic PICKUP confirmation (sets actual pickup time) when a truck dwelled at the origin then left. Pickups fire less often than deliveries because dispatchers usually click Pickup at the dock first.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_delivery_enabled", label: "Auto-confirm deliveries", type: "boolean",
    hint: "Allow automatic DELIVERY confirmation (marks delivered) when a truck dwelled at the destination then drove away. This is the highest-value leg -- it also stops a delivered-but-unmarked load from absorbing the truck's next fuel/tolls.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_always_on", label: "Run 24/7 (ignore the off-hours window)", type: "boolean",
    hint: "On = auto-confirm runs around the clock. Off = it only acts inside the off-hours window (evenings/overnight), leaving daytime stops to dispatchers.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_finalize_financials", label: "Close fuel/toll finances on auto-delivery", type: "boolean",
    hint: "On = an auto-delivery also finalizes (closes) the load's fuel/toll attribution, so no further fuel/tolls attach to a load that's done. Off = the load is marked delivered but a human closes the money. Only affects the delivery leg.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_require_appointment", label: "Require appointment-window match", type: "boolean",
    hint: "On (recommended) = only auto-confirm when the dwell lines up with the load's scheduled appointment window -- a strong precision guard. Off = confirm on physical evidence alone (more coverage, slightly more risk).",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_fresh_minutes", label: "GPS freshness required", type: "number", unit: "minutes",
    hint: "The truck's last GPS/telematics update must be at least this fresh for auto-confirm to trust the evidence. Too high and it may act on stale positions; too low and normal sync jitter blocks legitimate confirms.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_backlog_minutes", label: "Max dwell age to auto-confirm", type: "number", unit: "minutes",
    hint: "The live engine only auto-confirms a stop whose dwell happened within this window (default 180 = 3h). Older, already-left stops are NOT retroactively confirmed by the live engine -- those need a one-time backlog sweep.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.auto_pickup_radius_miles", label: "Pickup geofence radius", type: "number", unit: "miles",
    hint: "How close (in miles) a truck must get to the ORIGIN for the stop detector to count it as 'arrived at pickup'. This is the accuracy knob behind auto-pickup evidence -- tighter is more precise but can miss a large yard; wider is more forgiving but can trigger on a nearby stop. Default 5.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.auto_delivery_radius_miles", label: "Delivery geofence radius", type: "number", unit: "miles",
    hint: "Same idea for the DESTINATION: how close a truck must get before it counts as 'arrived at delivery'. Governs auto-delivery accuracy. Default 5.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_appt_grace_minutes", label: "Appointment-window grace", type: "number", unit: "minutes",
    hint: "When 'Require appointment-window match' is on, how early a truck may dwell before its scheduled appointment and still count as on-appointment (default 60). Only matters when the appointment guard is on.",
    section: "Auto-Confirm",
  },
  // ---- Off-hours window: ONLY applies when "Run 24/7" is OFF. Disabled in the
  // UI (and ignored by the engine) whenever always_on is true, so an admin can
  // see the configured window without it silently taking effect while 24/7 is on.
  {
    key: "fleet.autoconfirm_offhours_start", label: "Off-hours window start", type: "text", placeholder: "17:00",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "Local time (HH:MM, 24-hour) the off-hours auto-confirm window opens. Ignored while 'Run 24/7' is on -- turn 24/7 off to use a window.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_end", label: "Off-hours window end", type: "text", placeholder: "07:00",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "Local time (HH:MM, 24-hour) the window closes. A window that wraps past midnight (e.g. 17:00 -> 07:00) is supported. Ignored while 'Run 24/7' is on.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_include_weekends", label: "Include weekends in window", type: "boolean",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "When using a window (24/7 off), whether Saturday/Sunday count. Ignored while 'Run 24/7' is on.",
    section: "Auto-Confirm",
  },
  {
    key: "fleet.autoconfirm_offhours_timezone", label: "Auto-confirm timezone", type: "text", placeholder: "America/Chicago",
    disabledWhen: (d) => (d["fleet.autoconfirm_always_on"] ?? "true") === "true",
    hint: "IANA timezone the window and weekend rules are evaluated in (e.g. America/Chicago). Ignored while 'Run 24/7' is on. Falls back to the fleet default timezone if blank.",
    section: "Auto-Confirm",
  },

  {
    key: "fleet.min_fuel_stop_savings_usd", label: "Minimum fuel-stop savings", type: "currency", unit: "to add a stop",
    hint: "The optimizer only schedules a discretionary extra fuel stop if it saves at least this much versus not stopping (default $5). Raise it to avoid tiny-savings detours; lower it to chase smaller per-gallon wins. Does not affect genuinely mandatory stops (tank near reserve).",
    section: "Fuel Optimization",
  },

  {
    key: "fleet.critical_alert_sms_recipients", label: "Critical-alert SMS recipients", type: "text", placeholder: "+13125550199, +13125550142",
    hint: "Comma-separated phone numbers (E.164, e.g. +13125550199) that receive SMS for CRITICAL fleet alerts. Leave blank to disable critical-alert texting. Non-critical alerts are never texted.",
    section: "Alerts & Thresholds",
  },

  {
    key: "fleet.default_timezone", label: "Fleet default timezone", type: "text", placeholder: "America/Chicago",
    hint: "The fleet's base IANA timezone (e.g. America/Chicago), used as the fallback wherever a more specific timezone isn't set. Changing this shifts how times are interpreted fleet-wide -- set it to where the operation is actually run.",
    section: "Fleet Basics",
  },
];

const FIELD_SECTIONS = ["Fleet Basics", "Auto-Confirm", "Fuel Optimization", "Alerts & Thresholds", "Matching", "Lane History", "Feature Toggles"];

function defaultDraft(f) {
  if (f.type === "boolean") return "true";
  if (f.type === "select") return f.default ?? (f.options?.[0]?.value ?? "");
  if (f.type === "text") return "";
  return "0";
}

export default function FleetOperationalSettings() {
  const [rows, setRows] = useState({}); // sip_name -> { id, sip_value }
  const [drafts, setDrafts] = useState({}); // sip_name -> string being edited
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/system_properties/?limit=500`, authHeaders())
      .then((res) => {
        const records = res.data.records || res.data || [];
        const bySipName = {};
        records.forEach((r) => {
          if (FIELDS.some((f) => f.key === r.sip_name)) bySipName[r.sip_name] = r;
        });
        setRows(bySipName);
        const nextDrafts = {};
        FIELDS.forEach((f) => {
          nextDrafts[f.key] = bySipName[f.key]?.sip_value ?? defaultDraft(f);
        });
        setDrafts(nextDrafts);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirtyKeys = FIELDS.filter((f) => drafts[f.key] !== (rows[f.key]?.sip_value ?? defaultDraft(f))).map((f) => f.key);
  const hasInvalid = dirtyKeys.some((key) => {
    const f = FIELDS.find((x) => x.key === key);
    if (f.type === "boolean" || f.type === "select" || f.type === "text") return false;
    return drafts[key] === "" || Number.isNaN(Number(drafts[key]));
  });

  const save = () => {
    if (!dirtyKeys.length || hasInvalid) return;
    // A dirty key with no matching row means the initial load either hasn't
    // finished yet or failed silently for that property -- saving anyway
    // would crash on row.id below (same real bug FleetCostSettings.jsx
    // already guards against). Bail with a clear message instead of a
    // blank-screen exception.
    const missingRows = dirtyKeys.filter((key) => !rows[key]);
    if (missingRows.length) {
      setError("Some properties haven't finished loading yet -- refresh the page and try again.");
      return;
    }
    setSaving(true);
    setError(null);
    Promise.all(
      dirtyKeys.map((key) => {
        const f = FIELDS.find((x) => x.key === key);
        const row = rows[key];
        const value = (f.type === "boolean" || f.type === "select" || f.type === "text") ? drafts[key] : String(Number(drafts[key]));
        return axios.put(`${apiBaseUrl}/system_properties/${row.id}`, { sip_value: value }, authHeaders());
      })
    )
      .then(() => {
        setSavedAt(new Date());
        load();
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Fleet Operational Settings</h1>
          <div style={styles.subtitle}>
            How the platform behaves -- fuel-stop optimization, alert thresholds, matching radius, and
            feature toggles -- across trip planning, the Alerts page, and load matching. Real dollar cost
            inputs and hard spend ceilings live on{" "}
            <a href="/dispatch/fleet-cost-settings" style={{ color: "var(--brand)" }}>Fleet Cost Settings</a>.
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={load} disabled={loading}>
          <RefreshCw size={13} style={loading ? { animation: "lv-spin 0.8s linear infinite" } : undefined} /> Refresh
        </button>
      </div>

      {error && <div style={styles.errorBox}>Couldn't save: {error}</div>}

      {FIELD_SECTIONS.map((section) => {
        const sectionFields = FIELDS.filter((f) => f.section === section);
        if (!sectionFields.length) return null;
        return (
          <div key={section} style={styles.sectionBlock}>
            <div style={styles.sectionLabel}>{section}</div>
            <div style={styles.card}>
              {sectionFields.map((f, i) => {
                const draft = drafts[f.key] ?? defaultDraft(f);
                const isDirty = draft !== (rows[f.key]?.sip_value ?? defaultDraft(f));
                const isNum = f.type !== "boolean" && f.type !== "select" && f.type !== "text";
                const isInvalid = isNum && (draft === "" || Number.isNaN(Number(draft)));
                const isOn = draft === "true";
                // Fields that only apply under another setting (e.g. the off-hours
                // window while "Run 24/7" is off) grey out so a stale value can't
                // look active -- it is also ignored by the engine in that state.
                const dep = f.disabledWhen ? f.disabledWhen(drafts) : false;
                const dis = loading || saving || dep;
                return (
                  <div
                    key={f.key}
                    style={{ ...styles.row, opacity: dep ? 0.5 : 1, borderBottom: i === sectionFields.length - 1 ? "none" : "1px solid var(--line)" }}
                  >
                    <div style={styles.rowTop}>
                      <div style={styles.rowLabel}>{f.label}{dep && <span style={styles.depNote}> — n/a while 24/7 is on</span>}</div>
                      {f.type === "boolean" ? (
                        <button
                          type="button"
                          onClick={() => setDrafts((d) => ({ ...d, [f.key]: isOn ? "false" : "true" }))}
                          disabled={dis}
                          style={{ ...styles.toggleTrack, background: isOn ? "var(--brand)" : "var(--line)" }}
                          aria-pressed={isOn}
                        >
                          <span style={{ ...styles.toggleThumb, transform: isOn ? "translateX(16px)" : "translateX(0)" }} />
                        </button>
                      ) : f.type === "select" ? (
                        <select
                          value={draft}
                          onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                          disabled={dis}
                          style={{ ...styles.input, width: "auto", borderColor: isDirty ? "var(--brand)" : "var(--line)" }}
                        >
                          {f.options.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : f.type === "text" ? (
                        <input
                          type="text"
                          value={draft}
                          placeholder={f.placeholder || ""}
                          onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                          style={{ ...styles.input, width: 180, textAlign: "left", borderColor: isDirty ? "var(--brand)" : "var(--line)" }}
                          disabled={dis}
                        />
                      ) : (
                        <div style={styles.inputGroup}>
                          {f.type === "currency" && <span style={styles.dollarSign}>$</span>}
                          <input
                            type="number"
                            step="0.01"
                            value={draft}
                            onChange={(e) => setDrafts((d) => ({ ...d, [f.key]: e.target.value }))}
                            style={{ ...styles.input, borderColor: isInvalid ? "#dc2626" : isDirty ? "var(--brand)" : "var(--line)" }}
                            disabled={dis}
                          />
                          <span style={styles.unit}>{f.unit}</span>
                        </div>
                      )}
                    </div>
                    {f.hint && <div style={styles.rowHint}>{f.hint}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div style={styles.footer}>
        <button
          style={{ ...styles.saveBtn, opacity: !dirtyKeys.length || hasInvalid || saving ? 0.5 : 1 }}
          onClick={save}
          disabled={!dirtyKeys.length || hasInvalid || saving}
        >
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
        {!dirtyKeys.length && savedAt && (
          <span style={styles.savedNote}>Saved at {savedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
        )}
        {dirtyKeys.length > 0 && !hasInvalid && (
          <span style={styles.dirtyNote}>{dirtyKeys.length} unsaved change{dirtyKeys.length > 1 ? "s" : ""}</span>
        )}
      </div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 560, lineHeight: 1.5 },
  refreshBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 14px", cursor: "pointer",
    flexShrink: 0,
  },
  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },
  sectionBlock: { maxWidth: 760, marginBottom: 22 },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: 0.4, marginBottom: 8,
  },
  card: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", maxWidth: 760, overflow: "hidden",
  },
  row: { padding: "16px 20px" },
  rowTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 },
  rowLabel: { fontSize: 13.5, fontWeight: 600, color: "var(--ink)" },
  depNote: { fontSize: 11, fontWeight: 500, color: "var(--muted)", fontStyle: "italic" },
  rowHint: { fontSize: 12, color: "var(--muted)", marginTop: 6, lineHeight: 1.55, maxWidth: 640 },
  toggleTrack: {
    position: "relative", width: 36, height: 20, borderRadius: 999, border: "none", cursor: "pointer",
    padding: 2, flexShrink: 0,
  },
  toggleThumb: {
    display: "block", width: 16, height: 16, borderRadius: "50%", background: "#fff",
    transition: "transform 0.15s ease", boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
  },
  inputGroup: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 },
  dollarSign: { fontSize: 13, color: "var(--muted)", fontWeight: 600 },
  input: {
    width: 90, fontSize: 13.5, fontWeight: 600, color: "var(--ink)", padding: "6px 8px",
    border: "1px solid var(--line)", borderRadius: 7, textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  unit: { fontSize: 12, color: "var(--muted)", width: 62 },
  footer: { display: "flex", alignItems: "center", gap: 12, marginTop: 16 },
  saveBtn: {
    display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 700, color: "#fff",
    background: "var(--brand)", border: "none", borderRadius: 8, padding: "9px 18px", cursor: "pointer",
  },
  savedNote: { fontSize: 12, color: "#4cae6d", fontWeight: 600 },
  dirtyNote: { fontSize: 12, color: "var(--muted)" },
};
